import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { ExtendedTheme } from "@insite/mobius/ThemeProvider";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle, css, GlobalStyleComponent } from "styled-components";

export interface GlobalStyleProps {}

export interface FooterStyles {
    page?: MobiusPageProps;
}

const GlobalStyle = createGlobalStyle<GlobalStyleProps>`
    a:focus:not(:focus-visible),
    button:focus:not(:focus-visible),
    input[type="text"]:focus:not(:focus-visible), 
    input[type="password"]:focus:not(:focus-visible), 
    input[type="date"]:focus:not(:focus-visible),
     input[type="email"]:focus:not(:focus-visible), 
     input[type="number"]:focus:not(:focus-visible), 
     input[type="search"]:focus:not(:focus-visible), 
     input[type="tel"]:focus:not(:focus-visible), 
     input[type="time"]:focus:not(:focus-visible), 
     input[type="url"]:focus:not(:focus-visible), 
     textarea:focus:not(:focus-visible), 
     select:focus:not(:focus-visible) {
        outline: none !important;
    }

    input[type="text"]:focus:not(:focus-visible), 
    input[type="password"]:focus:not(:focus-visible), 
    input[type="date"]:focus:not(:focus-visible),
     input[type="email"]:focus:not(:focus-visible), 
     input[type="number"]:focus:not(:focus-visible), 
     input[type="search"]:focus:not(:focus-visible), 
     input[type="tel"]:focus:not(:focus-visible), 
     input[type="time"]:focus:not(:focus-visible), 
     input[type="url"]:focus:not(:focus-visible), 
     textarea:focus:not(:focus-visible), 
     select:focus:not(:focus-visible) {
        border:2px solid ${themeColors.colorInputFocus};
    }

    input[type="text"]:focus-visible:not(:focus), 
    input[type="password"]:focus-visible:not(:focus), 
    input[type="date"]:focus-visible:not(:focus),
     input[type="email"]:focus-visible:not(:focus), 
     input[type="number"]:focus-visible:not(:focus), 
     input[type="search"]:focus-visible:not(:focus), 
     input[type="tel"]:focus-visible:not(:focus), 
     input[type="time"]:focus-visible:not(:focus), 
     input[type="url"]:focus-visible:not(:focus), 
     textarea:focus-visible:not(:focus), 
     select:focus-visible:not(:focus),
     span[tabindex="0"]:focus-visible:not(:focus) {
        outline: none !important;
    }

    select[aria-invalid="true"]:not(.react-datetime-picker__inputGroup__input) {
        border: 1px solid ${themeColors.colorDanger} !important;
        background-color: ${themeColors.colorDangerLight} !important;
    }

    a:focus-visible,
    button:focus-visible,
    a:focus-visible,
    button:focus-visible,
    span[tabindex="0"]:focus-visible:not([role="checkbox"]){
        outline: 2px dashed ${themeColors.colorPrimaryButtonFocus} !important;
    }

    span[tabindex="0"][role="checkbox"]:focus-visible {
        outline: 2px solid ${themeColors.colorInputFocus} !important;
    }

    div[data-test-selector="page_HomePage"] main:before{
        top: 0;
        height: 100%;
    }

    div[data-test-selector="page_HomePage"] main {
        padding-top: 0 !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        background-color: ${themeColors.colorBackgroundLightGrey}!important; 
    }
    
    a:active,
    button:active {
        outline: none !important;
    }
    .active-nav {
        position: relative;
    }
    .active-nav:before {
        content: "";
        display: block;
        width: 100%;
        left: 0;
        top: -3.125rem;
        height: calc(100% + 3.125rem);
        background: rgba(0, 37, 84, .6);
        position: absolute;
        z-index: 1;
    }

    div[data-test-selector="page_CartPage"] {
        .active-nav:before {
            z-index: 2;
        }
    }

    body main{
        background-color: ${themeColors.colorBackgroundLightGrey};
        body{
            background-color: ${themeColors.colorBackgroundLightGrey};
        }
    }
        

    body div[data-test-selector="page_HomePage"]{
        main{
            background-color: ${themeColors.colorBackgroundLightGrey}; 
        }
    }
    
    div[data-test-selector="page_BrandsPage"] {
        div[data-test-selector="brandGallery"] {
            margin: 0;
            padding-top: 1.25rem;
        }
        .brand-see-more {
            display: none;
        }
    }

    @media (max-width: 1024px) {
        a:focus-visible,
        button:focus-visible,
        a:focus-visible,
        button:focus-visible{
            outline: none !important;
        }
        a {
           -webkit-tap-highlight-color: transparent;  
        }        
    }

` as GlobalStyleComponent<GlobalStyleProps, ExtendedTheme>;

export const footerStyles: FooterStyles = {
    page: {
        css: css`
            position: absolute;
            height: 2.5rem;
            width: 2.5rem;
            right: 1.25rem;
            top: -1.25rem;
            align-items: center;
            height: 1.25rem;
            padding: 0;
            border: 1px solid ${themeColors.colorButtonActive};
            border-radius: 100%;
            background-color: ${themeColors.colorYellow};
            justify-content: center;
            z-index: 99;
            cursor: pointer;
            animation: fadeIn .3s;
            transition: opacity .4s;

            &:hover {
                border: 1px solid ${themeColors.colorButtonActive};
                background-color: ${themeColors.colorYellow};
                opacity .9;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            top: 0;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },
};

const styles = footerStyles;

const ScrollArrow = () => {
    const [showScroll, setShowScroll] = useState(false);

    const checkScrollTop = () => {
        if (!showScroll && window.pageYOffset > 150) {
            setShowScroll(true);
        } else if (showScroll && window.pageYOffset <= 150) {
            setShowScroll(false);
        }
    };

    const scrollTop = () => {
        if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    if (typeof window !== "undefined") {
        window.addEventListener("scroll", checkScrollTop);
    }

    return (
        <>
            <Button
                {...styles.page}
                className="scrollTop"
                onClick={scrollTop}
                style={{ height: 40, top: -20, display: showScroll ? "flex" : "none" }}
            >
                <Icon src="ChevronUp" size={26} color="#4A4A4A" />
                <GlobalStyle />
            </Button>
            <GlobalStyle />
        </>
    );
};

export default ScrollArrow;
